import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import TableCell from '@material-ui/core/TableCell';
import MaterialTable, { MTablePagination, MTableToolbar } from 'material-table';
import CloseIcon from '@material-ui/icons/Close';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import SimpleSelect from '../inputs/SimpleSelect';
import CustomFilter from '../CustomFilter';
import MillsSalesCta from './MillsSalesCta';
import { useAuth } from '../../context/auth';

const overlayOptions = [
  { value: 'product_detail', label: 'Product Terminals Detail' },
  { value: 'product', label: 'Petroleum Product Terminals' },
  { value: 'additional_terminals', label: 'Additional Terminals' },
  { value: 'intermodal', label: 'Intermodal Freight Facilities' },
  { value: 'rack_pricing', label: 'Mills Rack Price Codes' },
];

const emptyRows = [];

const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const makeColumnKey = (fieldName) => `field_${String(fieldName).replace(/[^a-zA-Z0-9_]/g, '_')}`;

const moveItem = (items, sourceIndex, destinationIndex) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(destinationIndex, 0, movedItem);
  return nextItems;
};

const priorityColumnsByOverlay = {
  product_detail: [
    'Name',
    'Terminal Name',
    'Company',
    'City',
    'State',
    'Products',
    'Product',
    'Storage',
    'Tanks',
    'Crude Oil',
    'Asphalt',
    'Chemicals',
    'LNGs',
    'Propane',
    'Butane',
    'Refined Products',
    'Ethanol',
    'Biodiesel/Renewables',
  ],
  rack_pricing: [
    'City',
    'State',
    'Product',
    'Product Category',
    'Product Code',
    'Symbol',
    'Publication',
    'Price (USC/gal)',
    'Change',
    'Change Percent',
    'Assessment Date',
  ],
};

const applyPriorityColumns = (fieldNames, overlay) => {
  const priorityColumns = priorityColumnsByOverlay[overlay] || [];
  const normalizedFieldMap = fieldNames.reduce((acc, fieldName) => {
    acc[String(fieldName).trim().toLowerCase()] = fieldName;
    return acc;
  }, {});

  const orderedPriorityColumns = priorityColumns
    .map((fieldName) => normalizedFieldMap[String(fieldName).trim().toLowerCase()])
    .filter(Boolean);

  return [
    ...orderedPriorityColumns,
    ...fieldNames.filter((fieldName) => !orderedPriorityColumns.includes(fieldName)),
  ];
};

export default function OverlayDetailsTable({
  dataByOverlay,
  selectedOverlay,
  setSelectedOverlay,
  extendedOverlayAccess,
  millsOnly = false,
  rackPricingFilters = { publication: 'LATEST' },
  setRackPricingFilters,
}) {
  const { millsPricingAccess } = useAuth();
  const tableRef = useRef(null);
  const [visibleColumnsByOverlay, setVisibleColumnsByOverlay] = useState({});
  const [columnOrderByOverlay, setColumnOrderByOverlay] = useState({});
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [selectedDetailRow, setSelectedDetailRow] = useState(null);
  const availableOverlayOptions = useMemo(() => (
    millsOnly
      ? overlayOptions.filter((option) => option.value === 'rack_pricing')
      : extendedOverlayAccess
      ? overlayOptions
      : overlayOptions.filter((option) => option.value !== 'product_detail')
  ), [extendedOverlayAccess, millsOnly]);
  const selectedOption = availableOverlayOptions.find((option) => option.value === selectedOverlay);
  const effectiveSelectedOverlay = selectedOption
    ? selectedOverlay
    : availableOverlayOptions[0]?.value;
  const sourceRows = dataByOverlay[effectiveSelectedOverlay] || emptyRows;
  const showMillsSalesCta = effectiveSelectedOverlay === 'rack_pricing'
    && Number(millsPricingAccess) !== 1;
  const detailRows = useMemo(() => {
    if (!selectedDetailRow) return [];

    return Object.entries(selectedDetailRow)
      .filter(([key]) => key !== 'tableData' && key !== '_table_id')
      .map(([key, value]) => ({ key, value: formatValue(value) }));
  }, [selectedDetailRow]);

  useEffect(() => {
    if (!selectedOption && availableOverlayOptions.length > 0) {
      setSelectedOverlay(availableOverlayOptions[0].value);
    }
  }, [availableOverlayOptions, selectedOption, setSelectedOverlay]);

  const { baseColumns, rows, columnKeys } = useMemo(() => {
    const fieldNames = [];
    const seen = new Set();
    const visibleColumnKeys = visibleColumnsByOverlay[effectiveSelectedOverlay];
    const columnOrder = columnOrderByOverlay[effectiveSelectedOverlay];

    sourceRows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (key !== 'tableData' && key !== '_table_id' && !seen.has(key)) {
          seen.add(key);
          fieldNames.push(key);
        }
      });
    });

    const prioritizedFieldNames = applyPriorityColumns(fieldNames, effectiveSelectedOverlay);
    const orderedFieldNames = columnOrder
      ? [
          ...columnOrder.filter((fieldName) => seen.has(fieldName)),
          ...prioritizedFieldNames.filter((fieldName) => !columnOrder.includes(fieldName)),
        ]
      : prioritizedFieldNames;

    const fieldMap = fieldNames.reduce((acc, fieldName) => {
      acc[fieldName] = makeColumnKey(fieldName);
      return acc;
    }, {});

    return {
      baseColumns: orderedFieldNames.map((fieldName) => {
        const useLookup = effectiveSelectedOverlay === 'rack_pricing'
          && ['State', 'Product', 'Product Category'].includes(fieldName);
        const lookup = useLookup
          ? sourceRows.reduce((values, row) => {
              const value = formatValue(row[fieldName]);
              if (value) values[value] = value;
              return values;
            }, {})
          : undefined;

        return {
          rawTitle: fieldName,
          field: fieldMap[fieldName],
          hidden: visibleColumnKeys ? !visibleColumnKeys.includes(fieldMap[fieldName]) : false,
          lookup,
        };
      }),
      rows: sourceRows.map((row, rowIndex) => {
        const formattedRow = { _table_id: row._table_id ?? rowIndex };
        fieldNames.forEach((fieldName) => {
          formattedRow[fieldMap[fieldName]] = formatValue(row[fieldName]);
        });
        return formattedRow;
      }),
      columnKeys: fieldNames.map((fieldName) => fieldMap[fieldName]),
    };
  }, [sourceRows, effectiveSelectedOverlay, visibleColumnsByOverlay, columnOrderByOverlay]);
  const defaultPageSize = rows.length > 0 ? Math.min(25, rows.length) : 1;
  const pageSizeOptions = useMemo(() => {
    if (rows.length === 0) return [1];

    const options = [25, 50, 100].filter((size) => size <= rows.length);
    if (!options.includes(rows.length)) {
      options.push(rows.length);
    }
    return options.length ? options : [rows.length];
  }, [rows.length]);

  useEffect(() => {
    if (!visibleColumnsByOverlay[effectiveSelectedOverlay] && columnKeys.length > 0) {
      setVisibleColumnsByOverlay((prev) => ({
        ...prev,
        [effectiveSelectedOverlay]: columnKeys,
      }));
    }
  }, [columnKeys, effectiveSelectedOverlay, visibleColumnsByOverlay]);

  const setAllColumnsVisible = () => {
    setVisibleColumnsByOverlay((prev) => ({
      ...prev,
      [effectiveSelectedOverlay]: columnKeys,
    }));
  };

  const clearColumnFilter = (field) => {
    const tableColumn = tableRef.current?.state?.columns?.find((column) => column.field === field);

    if (tableColumn?.tableData && tableColumn.tableData.filterValue) {
      tableRef.current.onFilterChange(tableColumn.tableData.id, undefined);
    }
  };

  const clearColumnFilters = (fields) => {
    fields.forEach((field) => clearColumnFilter(field));
  };

  const setAllColumnsHidden = () => {
    clearColumnFilters(columnKeys);

    setVisibleColumnsByOverlay((prev) => ({
      ...prev,
      [effectiveSelectedOverlay]: [],
    }));
  };

  const handleColumnHiddenChange = (column, hidden) => {
    if (hidden) {
      clearColumnFilter(column.field);
    }

    setVisibleColumnsByOverlay((prev) => {
      const currentColumns = prev[effectiveSelectedOverlay] || columnKeys;
      const nextColumns = hidden
        ? currentColumns.filter((field) => field !== column.field)
        : [...new Set([...currentColumns, column.field])];

      return {
        ...prev,
        [effectiveSelectedOverlay]: nextColumns,
      };
    });
  };

  const hideColumn = (column) => {
    handleColumnHiddenChange(column, true);
  };

  const columns = baseColumns.map((column) => {
    const isPublicationColumn = effectiveSelectedOverlay === 'rack_pricing'
      && column.rawTitle === 'Publication';

    return {
      ...column,
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>{column.rawTitle}</span>
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              hideColumn(column);
            }}
            style={{ padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </span>
      ),
      ...(isPublicationColumn ? {
        filterComponent: () => (
          <Select
            value={rackPricingFilters.publication || 'LATEST'}
            onChange={(event) => setRackPricingFilters((previous) => ({
              ...previous,
              publication: event.target.value,
            }))}
            inputProps={{ 'aria-label': 'Select Mills price publication' }}
            style={{ minWidth: 110, fontSize: 13 }}
          >
            <MenuItem value="LATEST">Latest</MenuItem>
            <MenuItem value="TR">Morning</MenuItem>
            <MenuItem value="TE">Evening</MenuItem>
          </Select>
        ),
        filterCellStyle: { minWidth: 130 },
      } : {}),
    };
  });

  const toggleColumn = (column) => {
    handleColumnHiddenChange(column, !column.hidden);
  };

  const closeColumnMenu = () => {
    setColumnMenuAnchor(null);
  };

  const openColumnMenu = (event) => {
    const buttonBounds = event.currentTarget.getBoundingClientRect();
    setColumnMenuAnchor({
      top: buttonBounds.bottom,
      left: buttonBounds.right,
    });
  };

  const handleColumnDragged = (sourceIndex, destinationIndex) => {
    const visibleColumns = baseColumns.filter((column) => !column.hidden);
    const sourceColumn = visibleColumns[sourceIndex];
    const destinationColumn = visibleColumns[destinationIndex];

    if (!sourceColumn || !destinationColumn) return;

    setColumnOrderByOverlay((prev) => {
      const currentOrder = prev[effectiveSelectedOverlay] || baseColumns.map((column) => column.rawTitle);
      const sourceOrderIndex = currentOrder.indexOf(sourceColumn.rawTitle);
      const destinationOrderIndex = currentOrder.indexOf(destinationColumn.rawTitle);

      if (sourceOrderIndex === -1 || destinationOrderIndex === -1) return prev;

      return {
        ...prev,
        [effectiveSelectedOverlay]: moveItem(currentOrder, sourceOrderIndex, destinationOrderIndex),
      };
    });
  };

  const openRowDetails = (rowData) => {
    const rawRow = sourceRows.find((row, rowIndex) => (
      (row._table_id ?? rowIndex) === rowData._table_id
    ));

    setSelectedDetailRow(rawRow || null);
  };

  const closeRowDetails = () => {
    setSelectedDetailRow(null);
  };

  const getDisplayName = () => {
    if (!selectedDetailRow) return selectedOption ? selectedOption.label : 'Overlay Details';

    const nameKeys = [
      'Name',
      'Terminal Name',
      'Terminal_Name',
      'TERM_NAME',
      'Company',
      'Facility',
      'Operator',
      'Entity',
      'Owner',
      'Storage Site',
      'Terminal',
      'Facility Name',
      'FACILITY NAME',
    ];

    const key = nameKeys.find((fieldName) => (
      selectedDetailRow[fieldName] &&
      String(selectedDetailRow[fieldName]).trim().length > 0
    ));

    return key ? String(selectedDetailRow[key]).trim() : (selectedOption ? selectedOption.label : 'Overlay Details');
  };

  return (
    <Grid container spacing={1}>
      <Dialog open={Boolean(selectedDetailRow)} onClose={closeRowDetails}>
        <DialogTitle>{getDisplayName()}</DialogTitle>
        <DialogContent>
          {detailRows.map((row) => (
            <Typography key={row.key}>
              <strong>{row.key}:</strong> {row.value || 'N/A'}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRowDetails} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={closeColumnMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          key="column-list-title"
          disabled
          style={{ opacity: 1, fontWeight: 600, fontSize: 12 }}
        >
          Add or remove columns
        </MenuItem>
        <MenuItem
          key="select-all-columns"
          onClick={setAllColumnsVisible}
          disabled={columnKeys.length === 0}
        >
          <Typography color="primary">Select All</Typography>
        </MenuItem>
        <MenuItem
          key="deselect-all-columns"
          onClick={setAllColumnsHidden}
          disabled={columnKeys.length === 0}
        >
          <Typography color="primary">Deselect All</Typography>
        </MenuItem>
        <Divider />
        {columns.map((column) => (
          <MenuItem
            key={column.field}
            onClick={() => toggleColumn(column)}
          >
            <Checkbox
              color="primary"
              checked={!column.hidden}
              readOnly
              style={{ pointerEvents: 'none' }}
            />
            <span>{column.rawTitle}</span>
          </MenuItem>
        ))}
      </Menu>
      <Grid item xs={12} style={{ minWidth: 0, maxWidth: '100%' }}>
        <MaterialTable
          key={`${effectiveSelectedOverlay}-${defaultPageSize}`}
          tableRef={tableRef}
          style={{ maxWidth: '100%', overflow: 'hidden' }}
          title=""
          icons={{
            ViewColumn: forwardRef((props, ref) =>
              <ViewColumnIcon color="primary" {...props} ref={ref} />
            ),
          }}
          columns={columns}
          data={rows}
          options={{
            filtering: true,
            draggable: true,
            exportButton: false,
            pageSize: defaultPageSize,
            pageSizeOptions,
            emptyRowsWhenPaging: false,
            padding: 'dense',
            headerStyle: {
              paddingLeft: 8,
              paddingRight: 8,
              whiteSpace: 'nowrap',
            },
            cellStyle: {
              paddingLeft: 8,
              paddingRight: 8,
              fontSize: 13,
              whiteSpace: 'nowrap',
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            filterCellStyle: {
              paddingLeft: 8,
              paddingRight: 8,
            },
          }}
          onColumnDragged={handleColumnDragged}
          actions={[
            {
              icon: () => <ViewColumnIcon color="primary" />,
              tooltip: 'Show / Hide Columns',
              isFreeAction: true,
              onClick: openColumnMenu,
            },
          ]}
          components={{
            Container: props => <Paper {...props} elevation={0} square />,
            FilterRow: props => <CustomFilter {...props} />,
            Pagination: ({ classes: paginationClasses, ...props }) => (
              <TableCell
                colSpan={Math.max(columns.filter((column) => !column.hidden).length, 1)}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  padding: 0,
                  borderBottom: 0,
                  backgroundColor: 'transparent',
                }}
              >
                <div style={{ width: '100%' }}>
                  {showMillsSalesCta && (
                    <Grid container justify="center" style={{ width: '100%', padding: '12px 16px 4px' }}>
                      <MillsSalesCta />
                    </Grid>
                  )}
                  <MTablePagination
                    {...props}
                    classes={{ root: paginationClasses.root }}
                  />
                </div>
              </TableCell>
            ),
            Toolbar: props => (
              <Grid container alignItems="center" spacing={1} wrap="wrap">
                <Grid item xs={12}>
                  <Typography variant="h6" style={{ padding: '16px 24px 0 24px', fontWeight: 600 }}>
                    {selectedOption ? selectedOption.label : 'Overlay Details'}
                  </Typography>
                </Grid>
                <Grid item xs style={{ minWidth: 180 }}>
                  <MTableToolbar {...props} title="" />
                </Grid>
                <Grid item>
                  <SimpleSelect
                    value={effectiveSelectedOverlay}
                    setValue={setSelectedOverlay}
                    label="Overlay"
                    options={availableOverlayOptions}
                    allowNone={false}
                    compact
                  />
                </Grid>
                <Grid item style={{ paddingRight: 16, flexShrink: 0 }}>
                  <Typography color="textSecondary" variant="body2" noWrap>
                    {rows.length} rows
                  </Typography>
                </Grid>
              </Grid>
            ),
          }}
          onRowClick={(event, rowData) => openRowDetails(rowData)}
        />
      </Grid>
    </Grid>
  );
}
